// 클립보드 공유(설계서 §9.3.1) — 세션 화면의 **단축키·버튼·알림** 로직(SessionView 는 이 훅만 부른다).
//
// 단축키 규칙
//  - Ctrl+V(Mac 뷰어는 Cmd+V 도): V 키를 원격에 보내지 않고 내 클립보드를 보낸다 → 에이전트가 쓴 뒤 **자기 OS 의**
//    붙여넣기 단축키를 누른다. 원격 Ctrl 은 **떼지 않는다** — 에이전트가 주입 시점에 Ctrl 이 눌려 있으면 글자만 누른다
//    (뷰어가 떼 두면 복사 대기(최대 1.5초) 동안 사용자가 이어서 누른 Ctrl+A 가 원격에 'a' 로 들어갔다).
//    끝나고 아직 누르고 있으면 원격에도 한 번 더 눌러 둔다(에이전트가 Ctrl 로 감싸 뗀 경우의 안전망 — 이미 눌려 있으면 반복 입력일 뿐).
//  - Ctrl+C(Mac 뷰어는 Cmd+C 도): C 키도 보내지 않는다 → 에이전트가 복사 단축키를 누르고, 클립보드가 **바뀐 경우에만**
//    받아 내 PC 에 넣는다(명령 창의 "실행 중단" Ctrl+C 로 내 클립보드가 덮이지 않게 — 그때는 조용히 넘어간다).
//  - Shift·Alt 가 섞인 조합은 건드리지 않는다(Ctrl+Shift+V 등은 원격 앱의 고유 단축키일 수 있다).
//  - 메뉴에서 끄면 예전처럼 모든 키를 원격에 그대로 보낸다.
//
// 🔴 "클립보드의 주인" 판단(2026-10-20 사용자 보고 — 원격 탐색기 안의 Ctrl+C → Ctrl+V 가 깨졌다):
//    종전 Ctrl+V 는 **항상** 내 클립보드를 원격에 덮어썼다 → 원격에서 복사한 파일을 원격 안에서 붙이려 해도
//    원격 클립보드가 내 PC 것으로 바뀌었다(통합앱은 파일이 내 PC 를 거쳐 한 바퀴 돌았다).
//    ⇒ 원격에서 Ctrl+C 로 **클립보드가 실제로 바뀌면** "주인 = 원격" 으로 표시하고(`remoteOwns`), 그 상태의 Ctrl+V 는
//      **원격 클립보드 그대로** 붙인다(에이전트 `paste` — 쓰지 않고 원격 OS 의 붙여넣기 단축키만).
//      뷰어 창이 포커스를 잃으면(`localFocusLost` — 내 PC 의 다른 앱에서 새로 복사했을 수 있다) 표시를 지운다
//      → 다음 Ctrl+V 는 다시 내 클립보드를 보낸다.
//    구 에이전트(`remotePaste` 없음): Ctrl 뷰어는 V 키를 원격에 그대로 보낸다(원격 Ctrl 은 이미 눌려 있다 = 원격의 Ctrl+V).
//    Mac 뷰어의 Cmd+V 는 그렇게 보낼 수 없어(원격에서 Win+V 가 된다) 예전처럼 내 클립보드를 보낸다.
//
// Mac 뷰어의 Cmd: 지금까지 Cmd 는 원격 Windows 키(vk 91)로 바로 전달됐다. Cmd+V 를 가로채려면 Cmd 누름을 **잠시 보류**해야
// 한다(이미 보냈으면 원격에서 Win+Ctrl+V 같은 엉뚱한 조합이 된다). 보류 중 다른 키가 오면 그때 Cmd 를 보내고 이어서 보내므로,
// Cmd+V/C 외의 동작은 예전과 같다. Cmd 만 눌렀다 떼면 누름·뗌을 함께 보낸다(예전과 같은 결과).
import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';
import { IS_SHELL } from '../shell';
import type { Session } from '../sessions/types';
import { FEATURE, featureAllowed } from '../features';
import { loadFlag, saveFlag } from '../prefs';
import { contentFromDataTransfer, LocalClipboardBlocked, readLocal, writeLocal } from './local';
import { CLIP_ERR, ClipError, describeContent, type ClipContent } from './protocol';
import type { FileTransfer } from '../files/useFileTransfer';

/** 뷰어가 macOS 인지(Cmd 를 단축키 수정키로도 받는다). */
export const IS_MAC_VIEWER =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent);

/** 뷰어가 Windows 인지 — 세션 도구 막대를 작게 그린다(2026-09-29 사용자 요구 "윈도우는 메뉴 버튼 크기와 글씨가 작아야 함"). */
export const IS_WINDOWS_VIEWER =
  typeof navigator !== 'undefined' && /Win/i.test(navigator.platform || navigator.userAgent);

/** 단축키 연동 켬/끔을 기억하는 키(이 브라우저에만 — 사람마다 습관이 다르다). */
const SHORTCUT_PREF_KEY = 'rs.clip.shortcuts';
const VK_V = 86;
const VK_C = 67;
/** 원격에 눌려 있을 수 있는 Ctrl(일반·왼쪽·오른쪽). */
const CTRL_VKS = [17, 162, 163];
/** 브라우저에서 Ctrl+V 뒤 paste 이벤트를 기다리는 시간 — 오지 않으면(Mac 의 Ctrl+V) 클립보드 API 로 읽는다. */
const PASTE_EVENT_WAIT_MS = 150;
/** 알림 표시 시간. 버튼이 달린 알림은 누를 시간을 넉넉히 준다. */
const TOAST_MS = { ok: 2500, err: 6000, action: 15000 } as const;

export interface ClipToast {
  kind: 'ok' | 'err';
  text: string;
  /** 브라우저가 쓰기를 막았을 때 — 이 버튼을 누르면(=사용자 동작) 다시 쓴다. */
  action?: { label: string; run: () => void };
}

/** 세션 화면의 키 상태에 닿는 창구(SessionView 가 준다). */
export interface KeyBridge {
  /** 이 키가 지금 로컬에서 눌려 있는가(세션 화면이 추적하는 집합). */
  isHeld(vk: number): boolean;
  /** 추적 없이 원격에 키 이벤트만 보낸다(작업 뒤 Ctrl 다시 눌러 두기). */
  sendRaw(vk: number, down: boolean): void;
  /** 추적과 함께 원격에 보낸다(보류했던 Cmd 를 늦게 보낼 때). */
  forward(vk: number, down: boolean): void;
}

type MetaState = { phase: 'idle' } | { phase: 'pending' | 'forwarded' | 'consumed'; vk: number };

const loadPref = () => loadFlag(SHORTCUT_PREF_KEY, true);
const savePref = (on: boolean) => saveFlag(SHORTCUT_PREF_KEY, on);

/** 이 키 이벤트가 붙여넣기/복사 단축키인가. */
function shortcutOf(e: KeyboardEvent): 'paste' | 'copy' | null {
  if (e.altKey || e.shiftKey) {
    return null;
  }
  const ctrlOnly = e.ctrlKey && !e.metaKey;
  const cmdOnly = IS_MAC_VIEWER && e.metaKey && !e.ctrlKey;
  if (!ctrlOnly && !cmdOnly) {
    return null;
  }
  if (e.keyCode === VK_V) {
    return 'paste';
  }
  if (e.keyCode === VK_C) {
    return 'copy';
  }
  return null;
}

function errorText(e: unknown): string {
  return e instanceof ClipError ? e.message : `클립보드 공유에 실패했습니다: ${(e as Error)?.message ?? e}`;
}

export interface ClipboardShare {
  /** 이 세션에서 쓸 수 있는가(에이전트가 clip 채널을 열었고 제어가 허용됨). */
  available: boolean;
  busy: boolean;
  toast: ClipToast | null;
  dismissToast(): void;
  shortcutsOn: boolean;
  toggleShortcuts(): void;
  /** 키 이벤트를 먼저 본다. true 면 처리했으니 원격에 보내지 말 것. */
  handleKey(e: KeyboardEvent, down: boolean, vk: number): boolean;
  /** 숨김 입력칸의 paste 이벤트(브라우저 Ctrl+V 경로). */
  onPaste(e: React.ClipboardEvent): void;
  /** 버튼: 내 클립보드 → 원격(붙여넣기는 하지 않는다). */
  sendLocal(): Promise<void>;
  /** 버튼: 원격 클립보드 → 내 PC. */
  fetchRemote(): Promise<void>;
  /** 포커스를 잃었을 때 보류 상태를 비운다. */
  reset(): void;
  /**
   * 뷰어 창이 포커스를 잃었다(창 blur·탭 숨김) — 내 PC 의 다른 앱에서 새로 복사했을 수 있으므로
   * "클립보드 주인 = 원격" 표시를 지운다(다음 Ctrl+V 는 내 클립보드를 보낸다). `reset` 과 따로 둔다 —
   * reset 은 그리기 전환에서도 불리는데, 그때는 클립보드 주인이 바뀌지 않는다.
   */
  localFocusLost(): void;
}

/**
 * @param files 파일 전송(§9.4) — 있으면 Ctrl+V/C 가 **파일인지 먼저** 본다(붙여넣은 것이 파일이면 파일 전송,
 *              원격에서 복사한 것이 파일이면 파일 가져오기). 단축키 분기를 이 한 곳에 모아 두 기능이 서로 키를 뺏지 않게 한다.
 */
export function useClipboardShare(session: Session, keys: KeyBridge, files?: FileTransfer): ClipboardShare {
  const channel = session.client.clip;
  // 기능 권한(§14.19.2) `clipboard` 가 없으면 끈다 → Ctrl+V/C 는 예전처럼 원격에 그대로 간다.
  const available =
    !!session.clipCaps && !!channel && session.controlAllowed !== false &&
    featureAllowed(session.features, FEATURE.CLIPBOARD);
  // 에이전트가 "원격 클립보드 그대로 붙여넣기"(`paste`)를 아는가 — 구 에이전트는 hello 에 ops 가 없다.
  const remotePaste = !!session.clipCaps?.remotePaste;

  const [shortcutsOn, setShortcutsOn] = useState(loadPref);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<ClipToast | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);
  // 원격에 보내지 않은 키(V·C) — 그 키의 뗌도 보내지 않는다.
  const swallowed = useRef(new Set<number>());
  const meta = useRef<MetaState>({ phase: 'idle' });
  // 브라우저 Ctrl+V: paste 이벤트를 기다리는 중이면 대체 경로 타이머 id.
  const pasteWait = useRef<number | null>(null);
  // 지금 클립보드의 주인이 원격인가 — 원격 Ctrl+C 로 원격 클립보드가 바뀐 뒤 뷰어가 포커스를 잃지 않았다(파일 머리 주석).
  const remoteOwns = useRef(false);
  const keysRef = useRef(keys);
  keysRef.current = keys;

  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  const show = useCallback((t: ClipToast) => {
    window.clearTimeout(toastTimer.current);
    setToast(t);
    const ms = t.action ? TOAST_MS.action : TOAST_MS[t.kind];
    toastTimer.current = window.setTimeout(() => setToast(null), ms);
  }, []);
  const fail = useCallback((e: unknown) => show({ kind: 'err', text: errorText(e) }), [show]);
  const dismissToast = useCallback(() => {
    window.clearTimeout(toastTimer.current);
    setToast(null);
  }, []);

  /** 아직 로컬에서 누르고 있는 Ctrl 을 원격에도 다시 눌러 둔다(이어서 Ctrl+A 등을 누를 수 있게 — 안전망). */
  const reassertCtrl = useCallback(() => {
    for (const vk of CTRL_VKS) {
      if (keysRef.current.isHeld(vk)) {
        keysRef.current.sendRaw(vk, true);
      }
    }
  }, []);

  /** 원격에서 받은 내용을 내 PC 클립보드에 넣는다. 브라우저가 막으면 버튼 달린 알림으로 대신한다. */
  const deliverLocal = useCallback(
    async (c: ClipContent) => {
      try {
        await writeLocal(c);
        show({ kind: 'ok', text: `내 PC에 복사됨 — ${describeContent(c)}` });
      } catch (e) {
        if (!(e instanceof LocalClipboardBlocked)) {
          fail(e);
          return;
        }
        show({
          kind: 'ok',
          text: `원격 클립보드를 받았습니다 — ${describeContent(c)}`,
          action: {
            label: '내 PC에 복사',
            run: () => {
              writeLocal(c).then(() => show({ kind: 'ok', text: `내 PC에 복사됨 — ${describeContent(c)}` }), fail);
            },
          },
        });
      }
    },
    [show, fail],
  );

  const runPaste = useCallback(
    async (content: ClipContent | null) => {
      if (!channel) {
        return;
      }
      if (!content) {
        // 브라우저는 탐색기에서 복사한 파일을 붙여넣기로 주지 않는다(Chromium 실측 — 형식 목록조차 비어 온다).
        // 파일을 보내려던 것일 수 있으니 파일 기능이 있으면 대신할 방법을 알린다.
        if (!IS_SHELL && files?.showPasteHint()) {
          return;
        }
        fail(new ClipError(CLIP_ERR.LOCAL_EMPTY));
        return;
      }
      setBusy(true);
      try {
        await channel.put(content, true);
        remoteOwns.current = false; // 원격 클립보드 = 내 것이 됐다 — 주인은 다시 내 PC
        show({ kind: 'ok', text: `붙여넣기 — ${describeContent(content)}` });
      } catch (e) {
        fail(e);
      } finally {
        reassertCtrl();
        setBusy(false);
      }
    },
    [channel, files, reassertCtrl, show, fail],
  );

  const runCopy = useCallback(async () => {
    if (!channel) {
      return;
    }
    setBusy(true);
    // 결과를 기다리는 동안 이어 누른 Ctrl+V 도 원격 것을 붙이도록 **미리** 주인을 원격으로 둔다(복사 대기 최대 1.5초).
    // 복사된 것이 없었으면(실행 중단 Ctrl+C 등) 원래 주인으로 되돌린다 — 그때 원격 클립보드는 바뀌지 않았다.
    const ownerBefore = remoteOwns.current;
    remoteOwns.current = true;
    try {
      if (files?.canPull) {
        // 파일 채널이 복사 단축키를 누르고 결과를 가른다: 파일이면 알림([내 PC로 받기]/[저장]),
        // 복사된 게 없으면 조용히, 파일이 아니면 **이미 복사됐으니** 단축키 없이 클립보드 내용을 가져온다.
        const r = await files.copyRemote();
        if (r === 'unchanged') {
          remoteOwns.current = ownerBefore;
        } else if (r === 'not_files') {
          await deliverLocal(await channel.get(false));
        }
        return;
      }
      await deliverLocal(await channel.get(true));
    } catch (e) {
      // 복사할 것이 없었다 = Ctrl+C 를 "실행 중단" 등 다른 뜻으로 눌렀을 수 있다 → 조용히 넘어간다.
      if (e instanceof ClipError && e.code === CLIP_ERR.UNCHANGED) {
        remoteOwns.current = ownerBefore;
      } else {
        fail(e);
      }
    } finally {
      reassertCtrl();
      setBusy(false);
    }
  }, [channel, files, reassertCtrl, deliverLocal, fail]);

  const readAndPaste = useCallback(() => {
    readLocal().then(runPaste, fail);
  }, [runPaste, fail]);

  /** 원격 클립보드 그대로 원격에 붙여넣기(주인 = 원격일 때의 Ctrl+V). 원격의 평소 붙여넣기처럼 알림은 띄우지 않는다. */
  const pasteRemoteOwn = useCallback(async () => {
    if (!channel) {
      return;
    }
    try {
      await channel.pasteRemote();
    } catch (e) {
      fail(e);
    } finally {
      reassertCtrl();
    }
  }, [channel, reassertCtrl, fail]);

  /**
   * 통합앱 Ctrl+V — 탐색기에서 복사한 **파일**이면 파일 전송(네이티브가 OS 파일 클립보드를 읽는다, §9.4),
   * 아니면 텍스트·이미지(§9.3.1). 파일이 먼저다: 탐색기 복사는 텍스트 형식을 싣지 않으므로 둘이 겹칠 일은 드물다.
   */
  const pasteInShell = useCallback(async () => {
    if (files && (await files.sendNativeClipboard())) {
      reassertCtrl();
      return;
    }
    readAndPaste();
  }, [files, reassertCtrl, readAndPaste]);

  const localFocusLost = useCallback(() => {
    remoteOwns.current = false;
  }, []);

  const reset = useCallback(() => {
    meta.current = { phase: 'idle' };
    swallowed.current.clear();
    if (pasteWait.current !== null) {
      window.clearTimeout(pasteWait.current);
      pasteWait.current = null;
    }
  }, []);

  const handleKey = useCallback(
    (e: KeyboardEvent, down: boolean, vk: number): boolean => {
      if (!available || !shortcutsOn) {
        return false;
      }
      const m = meta.current;

      if (!down) {
        if (swallowed.current.delete(vk)) {
          e.preventDefault();
          return true;
        }
        if (IS_MAC_VIEWER && e.key === 'Meta' && m.phase !== 'idle') {
          meta.current = { phase: 'idle' };
          if (m.phase === 'pending') {
            // Cmd 만 눌렀다 뗐다 — 예전처럼 원격에 누름·뗌을 보낸다.
            keysRef.current.forward(m.vk, true);
            keysRef.current.forward(m.vk, false);
            e.preventDefault();
            return true;
          }
          if (m.phase === 'consumed') {
            e.preventDefault();
            return true; // 원격에 보낸 적 없는 Cmd — 뗌도 보내지 않는다
          }
          return false; // forwarded — 평소대로 뗌을 보낸다
        }
        return false;
      }

      // ── 누름 ──
      if (IS_MAC_VIEWER && e.key === 'Meta') {
        if (m.phase === 'forwarded') {
          return false; // 이미 보낸 Cmd 의 자동 반복 — 평소대로
        }
        if (m.phase === 'idle') {
          meta.current = { phase: 'pending', vk };
        }
        e.preventDefault();
        return true;
      }

      const action = shortcutOf(e);
      if (!action) {
        // Cmd 를 보류 중이었다면 이제 보내고, 이 키는 평소대로 이어서 보낸다.
        if (m.phase === 'pending' || m.phase === 'consumed') {
          keysRef.current.forward(m.vk, true);
          meta.current = { phase: 'forwarded', vk: m.vk };
        }
        return false;
      }
      // 주인 = 원격인데 에이전트가 `paste` 를 모른다(구 에이전트) — Ctrl 조합이면 V 를 원격에 **그대로** 보낸다
      // (원격 Ctrl 은 이미 눌려 있다 = 원격의 Ctrl+V). Cmd+V 는 원격에서 Win+V 가 되므로 아래 예전 경로로 간다.
      if (action === 'paste' && remoteOwns.current && !remotePaste && e.ctrlKey && !e.metaKey) {
        return false;
      }
      if (m.phase === 'pending') {
        meta.current = { phase: 'consumed', vk: m.vk };
      }
      swallowed.current.add(vk);
      if (e.repeat) {
        e.preventDefault(); // 누른 채 반복 — 한 번만 처리한다
        return true;
      }
      if (action === 'copy') {
        e.preventDefault();
        void runCopy();
        return true;
      }
      // 붙여넣기 — 주인 = 원격이면 원격 클립보드 그대로(내 클립보드로 덮지 않는다, 파일 머리 주석).
      if (remoteOwns.current && remotePaste) {
        e.preventDefault();
        void pasteRemoteOwn();
        return true;
      }
      if (IS_SHELL) {
        e.preventDefault();
        void pasteInShell();
        return true;
      }
      // 브라우저: 기본 동작을 **막지 않아야** paste 이벤트가 온다(권한 창 없이 클립보드를 받는 유일한 길).
      // 오지 않으면(Mac 의 Ctrl+V 는 붙여넣기가 아니다) 잠시 뒤 클립보드 API 로 읽는다.
      if (pasteWait.current !== null) {
        window.clearTimeout(pasteWait.current);
      }
      pasteWait.current = window.setTimeout(() => {
        pasteWait.current = null;
        readAndPaste();
      }, PASTE_EVENT_WAIT_MS);
      return true;
    },
    [available, shortcutsOn, remotePaste, runCopy, readAndPaste, pasteInShell, pasteRemoteOwn],
  );

  const onPaste = useCallback(
    (e: React.ClipboardEvent) => {
      // 숨김 입력칸에 글자가 들어가지 않게 항상 막는다.
      e.preventDefault();
      if (pasteWait.current === null) {
        return; // 우리가 기다리던 붙여넣기가 아니다
      }
      window.clearTimeout(pasteWait.current);
      pasteWait.current = null;
      // 탐색기에서 복사한 **파일**이면 파일 전송(원격이 받은 뒤 붙여넣는다). ⚠️ 이벤트 처리 중에 불러야 목록이 읽힌다.
      if (files?.sendDataTransfer(e.clipboardData, { kind: 'paste' })) {
        reassertCtrl();
        return;
      }
      contentFromDataTransfer(e.clipboardData).then(runPaste, fail);
    },
    [files, runPaste, reassertCtrl, fail],
  );

  const sendLocal = useCallback(async () => {
    if (!channel) {
      return;
    }
    setBusy(true);
    try {
      const c = await readLocal();
      if (!c) {
        throw new ClipError(CLIP_ERR.LOCAL_EMPTY);
      }
      await channel.put(c, false);
      remoteOwns.current = false;
      show({ kind: 'ok', text: `원격 클립보드에 넣었습니다 — ${describeContent(c)}. 원격 화면에서 붙여넣기 하세요.` });
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }, [channel, show, fail]);

  const fetchRemote = useCallback(async () => {
    if (!channel) {
      return;
    }
    setBusy(true);
    try {
      await deliverLocal(await channel.get(false));
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }, [channel, deliverLocal, fail]);

  const toggleShortcuts = useCallback(() => {
    setShortcutsOn((on) => {
      savePref(!on);
      return !on;
    });
    reset();
  }, [reset]);

  return {
    available,
    busy,
    toast,
    dismissToast,
    shortcutsOn,
    toggleShortcuts,
    handleKey,
    onPaste,
    sendLocal,
    fetchRemote,
    reset,
    localFocusLost,
  };
}
